// src/components/UserLocationReporter.tsx
import React, { useEffect } from "react";
import { useAuth } from "../context/auth-context";

const API_BASE = "https://api.eva-googlemarine.com";
const ACCESS_TOKEN_KEY = "access_token";
const INTERVAL_MS = 60_000; // 1 minuto

type Coords = {
  latitude: number;
  longitude: number;
};

/**
 * Se quiser usar uma API própria de localização,
 * é só trocar a implementação desta função.
 */
async function getCurrentLocation(): Promise<Coords | null> {
  // 👉 Versão usando API nativa do navegador (Geolocation)
  if (!("geolocation" in navigator)) {
    return null;
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        });
      },
      () => {
        resolve(null);
      },
      {
        enableHighAccuracy: true,
        timeout: 7000,
        maximumAge: 30_000,
      }
    );
  });

  // 👉 Se quiser usar uma API própria, algo como:
  //
  // const res = await fetch("SUA_API_DE_LOCALIZACAO");
  // const json = await res.json();
  // return { latitude: json.lat, longitude: json.lng };
}

const UserLocationReporter: React.FC = () => {
  const { sessao } = useAuth();

  useEffect(() => {
    // se não tiver sessão, não faz nada
    if (!sessao) return;

    let stopped = false;
    let intervalId: number | undefined;

    const tick = async () => {
      try {
        if (stopped) return;

        const token = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (!token) {
          if (process.env.NODE_ENV !== "production") {
            console.warn("[LocationReporter] sem access_token no localStorage");
          }
          return;
        }

        const coords = await getCurrentLocation();
        if (!coords) {
          if (process.env.NODE_ENV !== "production") {
            console.warn("[LocationReporter] não conseguiu obter localização");
          }
          return;
        }

        await fetch(`${API_BASE}/geral/atualizar-localizacao-usuario`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            latitude: coords.latitude,
            longitude: coords.longitude,
          }),
        }).catch((e) => {
          if (process.env.NODE_ENV !== "production") {
            console.warn("[LocationReporter] falha ao enviar localização:", e);
          }
        });
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[LocationReporter] erro inesperado:", e);
        }
      }
    };

    // dispara uma vez logo que loga
    tick();

    // agenda para a cada 1 minuto
    intervalId = window.setInterval(tick, INTERVAL_MS);

    // cleanup
    return () => {
      stopped = true;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [sessao]); // quando deslogar / trocar usuário, efeito reinicia

  // componente "fantasma": não renderiza nada
  return null;
};

export default UserLocationReporter;
